import { Link, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import FormInput from '@/Components/Form/FormInput';
import FormTextarea from '@/Components/Form/FormTextarea';
import FormSelect from '@/Components/Form/FormSelect';
import FormSection from '@/Components/Form/FormSection';

export default function SuppliersEdit({ supplier, countries, serviceTypes }) {
    const { data, setData, post, processing, errors } = useForm({
        _method: 'PUT',
        name: supplier.name || '',
        code: supplier.code || '',
        service_types: supplier.service_types || [],
        contact_person: supplier.contact_person || '',
        contact_designation: supplier.contact_designation || '',
        contact_email: supplier.contact_email || '',
        contact_phone: supplier.contact_phone || '',
        contact_phone_alt: supplier.contact_phone_alt || '',
        country_id: supplier.country_id || '',
        state: supplier.state || '',
        city: supplier.city || '',
        zip: supplier.zip || '',
        address: supplier.address || '',
        gst_no: supplier.gst_no || '',
        pan_no: supplier.pan_no || '',
        bank_name: supplier.bank_name || '',
        bank_branch: supplier.bank_branch || '',
        account_name: supplier.account_name || '',
        account_number: supplier.account_number || '',
        ifsc_code: supplier.ifsc_code || '',
        swift_code: supplier.swift_code || '',
        agreement_file: null,
        remarks: supplier.remarks || '',
        is_active: supplier.is_active,
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        post(`/suppliers/${supplier.id}`, {
            forceFormData: true,
        });
    };

    const handleServiceTypeChange = (type) => {
        const newTypes = data.service_types.includes(type)
            ? data.service_types.filter(t => t !== type)
            : [...data.service_types, type];
        setData('service_types', newTypes);
    };

    return (
        <AppLayout title="Edit Supplier">
            <PageHeader
                title="Edit Supplier"
                subtitle={`Editing ${supplier.name}`}
                breadcrumbs={[
                    { label: 'Travel' },
                    { label: 'Suppliers', href: '/suppliers' },
                    { label: 'Edit' },
                ]}
            />

            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-lg-8">
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Basic Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <FormInput
                                        className="col-md-8"
                                        label="Supplier Name"
                                        name="name"
                                        value={data.name}
                                        onChange={(v) => setData('name', v)}
                                        error={errors.name}
                                        required
                                        placeholder="Enter supplier name"
                                    />
                                    <FormInput
                                        className="col-md-4"
                                        label="Supplier Code"
                                        name="code"
                                        value={data.code}
                                        onChange={(v) => setData('code', v.toUpperCase())}
                                        error={errors.code}
                                        placeholder="e.g., SUP001"
                                    />
                                    <div className="col-12">
                                        <label className="form-label">Service Types <span className="text-danger">*</span></label>
                                        {errors.service_types && (
                                            <div className="alert alert-danger py-2 mb-2">{errors.service_types}</div>
                                        )}
                                        <div className="row g-2">
                                            {Object.entries(serviceTypes).map(([key, label]) => (
                                                <div key={key} className="col-md-3 col-6">
                                                    <div
                                                        className={`card h-100 cursor-pointer ${data.service_types.includes(key) ? 'border-primary bg-primary-subtle' : ''}`}
                                                        onClick={() => handleServiceTypeChange(key)}
                                                    >
                                                        <div className="card-body py-2 px-3">
                                                            <div className="form-check mb-0">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={data.service_types.includes(key)}
                                                                    onChange={() => handleServiceTypeChange(key)}
                                                                />
                                                                <label className="form-check-label small">
                                                                    {label}
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <FormSection title="Contact Person">
                            <FormInput
                                className="col-md-6"
                                label="Contact Name"
                                name="contact_person"
                                value={data.contact_person}
                                onChange={(v) => setData('contact_person', v)}
                                placeholder="Full name"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Designation"
                                name="contact_designation"
                                value={data.contact_designation}
                                onChange={(v) => setData('contact_designation', v)}
                                placeholder="e.g., Manager"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Email"
                                name="contact_email"
                                type="email"
                                value={data.contact_email}
                                onChange={(v) => setData('contact_email', v)}
                                error={errors.contact_email}
                                placeholder="email@example.com"
                            />
                            <FormInput
                                className="col-md-3"
                                label="Phone"
                                name="contact_phone"
                                value={data.contact_phone}
                                onChange={(v) => setData('contact_phone', v)}
                                placeholder="+91 9876543210"
                            />
                            <FormInput
                                className="col-md-3"
                                label="Alt. Phone"
                                name="contact_phone_alt"
                                value={data.contact_phone_alt}
                                onChange={(v) => setData('contact_phone_alt', v)}
                                placeholder="Alternative"
                            />
                        </FormSection>

                        <FormSection title="Address">
                            <FormSelect
                                className="col-md-6"
                                label="Country"
                                name="country_id"
                                value={data.country_id}
                                onChange={(v) => setData('country_id', v)}
                                options={countries}
                                searchable
                                placeholder="Select Country"
                            />
                            <FormInput
                                className="col-md-6"
                                label="State"
                                name="state"
                                value={data.state}
                                onChange={(v) => setData('state', v)}
                                placeholder="State/Province"
                            />
                            <FormInput
                                className="col-md-6"
                                label="City"
                                name="city"
                                value={data.city}
                                onChange={(v) => setData('city', v)}
                                placeholder="City"
                            />
                            <FormInput
                                className="col-md-6"
                                label="ZIP/Postal Code"
                                name="zip"
                                value={data.zip}
                                onChange={(v) => setData('zip', v)}
                                placeholder="ZIP Code"
                            />
                            <FormTextarea
                                className="col-12"
                                label="Address"
                                name="address"
                                value={data.address}
                                onChange={(v) => setData('address', v)}
                                rows={2}
                                placeholder="Full address"
                            />
                        </FormSection>

                        <FormSection title="Bank Details">
                            <FormInput
                                className="col-md-6"
                                label="Bank Name"
                                name="bank_name"
                                value={data.bank_name}
                                onChange={(v) => setData('bank_name', v)}
                                placeholder="Bank name"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Branch"
                                name="bank_branch"
                                value={data.bank_branch}
                                onChange={(v) => setData('bank_branch', v)}
                                placeholder="Branch name"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Account Name"
                                name="account_name"
                                value={data.account_name}
                                onChange={(v) => setData('account_name', v)}
                                placeholder="Account holder name"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Account Number"
                                name="account_number"
                                value={data.account_number}
                                onChange={(v) => setData('account_number', v)}
                                placeholder="Account number"
                            />
                            <FormInput
                                className="col-md-6"
                                label="IFSC Code"
                                name="ifsc_code"
                                value={data.ifsc_code}
                                onChange={(v) => setData('ifsc_code', v.toUpperCase())}
                                placeholder="IFSC code"
                            />
                            <FormInput
                                className="col-md-6"
                                label="SWIFT Code"
                                name="swift_code"
                                value={data.swift_code}
                                onChange={(v) => setData('swift_code', v.toUpperCase())}
                                placeholder="SWIFT code (international)"
                            />
                        </FormSection>

                        <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary" disabled={processing}>
                                {processing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-lg me-1"></i>
                                        Save Changes
                                    </>
                                )}
                            </button>
                            <Link href="/suppliers" className="btn btn-outline-secondary">
                                Cancel
                            </Link>
                        </div>
                    </div>

                    <div className="col-lg-4">
                        <FormSection title="Business Details">
                            <FormInput
                                className="col-12"
                                label="GST Number"
                                name="gst_no"
                                value={data.gst_no}
                                onChange={(v) => setData('gst_no', v.toUpperCase())}
                                placeholder="e.g., 29ABCDE1234F1Z5"
                            />
                            <FormInput
                                className="col-12"
                                label="PAN Number"
                                name="pan_no"
                                value={data.pan_no}
                                onChange={(v) => setData('pan_no', v.toUpperCase())}
                                placeholder="e.g., ABCDE1234F"
                            />
                            <div className="col-12">
                                <label className="form-label">Agreement File</label>
                                {supplier.agreement_file && (
                                    <div className="mb-2">
                                        <a
                                            href={`/storage/${supplier.agreement_file}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-primary"
                                        >
                                            <i className="bi bi-file-earmark-pdf me-1"></i>
                                            View Current File
                                        </a>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    className={`form-control ${errors.agreement_file ? 'is-invalid' : ''}`}
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => setData('agreement_file', e.target.files[0])}
                                />
                                <small className="text-muted">Leave empty to keep current file</small>
                                {errors.agreement_file && <div className="invalid-feedback">{errors.agreement_file}</div>}
                            </div>
                        </FormSection>

                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Status & Notes</h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <div className="form-check form-switch">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="is_active"
                                            checked={data.is_active}
                                            onChange={(e) => setData('is_active', e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="is_active">
                                            Active
                                        </label>
                                    </div>
                                </div>
                                <FormTextarea
                                    label="Remarks"
                                    name="remarks"
                                    value={data.remarks}
                                    onChange={(v) => setData('remarks', v)}
                                    rows={4}
                                    placeholder="Internal notes about this supplier..."
                                />
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Info</h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-2">
                                    <small className="text-muted d-block">Created</small>
                                    <span>{new Date(supplier.created_at).toLocaleDateString()}</span>
                                </div>
                                <div>
                                    <small className="text-muted d-block">Last Updated</small>
                                    <span>{new Date(supplier.updated_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </AppLayout>
    );
}
